import { inject, Injectable, signal } from "@angular/core";
import { CustomFormArray, CustomFormControl, CustomFormGroup } from "@tilby/tilby-ui-lib/components/tilby-magic-form";
import { ItemsFormWithCustomVariations, ItemsVariationsWithCustomVariationsValue, ItemsVariationValuesFormArray, ItemVariationsFields, ItemVariationsFormGroups, ItemVariationsValidators, VariationsFormGroup } from "../item-variations.model";
import { AlternativeTranslate, Categories, Items, ItemsVariations, ItemsVariationValues } from "tilby-models";
import { ItemDataCatalogService } from "src/app/features/items/services/item-data-catalog.service";
import { Validators } from "@angular/forms";
import { v4 as generateUuid } from 'uuid';


@Injectable()
export class VariationsFormService {

    private readonly itemDataCatalogService = inject(ItemDataCatalogService);

    private variationsForm!: ItemVariationsFormGroups;

    get form() {
        return this.variationsForm;
    }

    variationLinkableItems: Array<Items> = [];
    itemsWithVariation: Array<Items> = [];
    categoriesWithVariation: Array<Categories> = [];

    initialVariations: Array<ItemsVariations> = [];
    
    validators: ItemVariationsValidators = {
        nameMaxLength: 255,
        nameVariantValueMaxLength: 255,
        minValueMinQt: 0,
        minValueMaxQt: 0,
        minPriceCombination: 0,
        skuMaxLength: 64,
        nameAddedVariantMaxLength: 255,

        // --------
        provMinLength: 0,
        provMaxLength: 255,
        provMaxLengthArray: new Array(10).fill(2)
    };


    variationNameChanged = signal<{ index: number }>({ index: 0 });
    variationValueNameChanged = signal<{ index: number }>({ index: 0 });
    variationAdded = signal<any>(null);
    variationsOrderChanged = signal<any>(null);
    variationValuesOrderChanged = signal<number>(0);
    variationsFormReady = signal(false);

    createForm(variations: Array<ItemsVariations> = []) {
        this.variationLinkableItems = this.itemDataCatalogService.getDataByKey("items")?.filter((item) => (item.uuid && !item.combinations?.length)) || [];
        this.itemsWithVariation = this.itemDataCatalogService.getDataByKey("items")?.filter((item) => (item.variations?.length && item.variations.length > 0)) || [];
        this.categoriesWithVariation = this.itemDataCatalogService.getDataByKey("categories")?.filter((category) => (category.variations?.length && category.variations.length > 0)) || [];
        this.initialVariations = variations || [];

        this.variationsForm = new CustomFormGroup<ItemsFormWithCustomVariations>({
            variations: new CustomFormArray<CustomFormGroup<ItemsVariationsWithCustomVariationsValue>>(
                []
            )
        });
        
        if(variations?.length) this.initVariation(variations);

        this.variationsFormReady.set(true);
        return this.variationsForm;
    }

    resetFormValues(originalVariations: ItemsVariations[] = []) {
        if (!this.variationsForm) return;

        this.variationsFormReady.set(false);
        this.variationsArray.clear();

        if (originalVariations && originalVariations.length > 0) {
            this.initVariation(originalVariations);
        }

        this.variationsFormReady.set(true);
    }

    addNewVariation(variationName: string = '') {
        this.variationsArray?.push(
            new CustomFormGroup<ItemsVariationsWithCustomVariationsValue>(
                {
                    name: new CustomFormControl(
                        variationName? variationName: null, 
                        { validators: [Validators.required, Validators.maxLength(this.validators.nameMaxLength)] }, 
                    ),
                    alternative_names: new CustomFormControl(
                        []
                    ),
                    id: new CustomFormControl(
                        0 // variante (NON aggiungerlo al body per POST/PUT)
                    ),
                    tmp_id: new CustomFormControl(
                        generateUuid() //serve per la sincronizzazione con i valori variante (NON aggiungerlo al body per POST/PUT)
                    ),
                    default_exit: new CustomFormControl(
                        null
                    ),
                    required: new CustomFormControl(
                        false
                    ),
                    min_quantity: new CustomFormControl(
                        null
                    ),
                    max_quantity: new CustomFormControl(
                        null
                    ),
                    variation_values: new CustomFormArray<CustomFormGroup<ItemsVariationValuesFormArray>>(
                        []
                    ),
                    index: new CustomFormControl(
                        this.variationsArray.length + 1
                    ),
                    disable_quantity_value: new CustomFormControl(
                        false
                    ),
                    expanded: new CustomFormControl(
                        !!!variationName
                    ),
                    variation_value_search: new CustomFormControl(null)
                }
            )
        )
    }

    removeAllVariations() {
        for (let i = (this.variationsArray?.value.length || 0) - 1; i >= 0; i--) {
            this.removeVariation(i);
        }
    }

    removeVariation(variationIndex: number) {
        this.variationsArray?.removeAt(variationIndex);
    }

    removeVariationValue(variationIndex: number, variationValueIndex: number) {
        this.getVariationValues(variationIndex)?.removeAt(variationValueIndex);
    }

    addVariationValue(variantIndex: number, variationValueName: string = '') {
        const index = this.getVariationValues(variantIndex)?.length || 0;
        const variationValue = new CustomFormGroup<ItemsVariationValuesFormArray>(
            {
                value: new CustomFormControl(
                    variationValueName? variationValueName: null,
                    { validators: [Validators.required, Validators.maxLength(this.validators.nameVariantValueMaxLength)] }, 
                ),
                alternative_names: new CustomFormControl(
                    []
                ),
                id: new CustomFormControl(
                    0 // (NON aggiungerlo al body per POST/PUT)
                ),
                tmp_id: new CustomFormControl(
                    generateUuid() //serve per la sincronizzazione con i valori variante (NON aggiungerlo al body per POST/PUT)
                ),
                price_difference: new CustomFormControl(
                    null
                ),
                price_difference_type: new CustomFormControl(
                    null
                ),
                default_value: new CustomFormControl(
                    false
                ),
                _linkedItemName: new CustomFormControl(
                    null
                ),
                linked_item_uuid: new CustomFormControl(
                    null
                ),
                _image: new CustomFormControl(
                    null
                ),
                image_url: new CustomFormControl(
                    null
                ),
                unit_stock_quantity: new CustomFormControl(
                    null
                ),
                index: new CustomFormControl(
                    index + 1
                ),
                expanded: new CustomFormControl(!!!variationValueName)
            }
        )
        this.getVariationValues(variantIndex)?.push(variationValue);
    }

    copyVariation(variation: Array<ItemsVariations>) {
        this.initVariation(variation);
    }

    initVariation(variations: ItemVariationsFields[]) {
        const sortedVariations = [...variations].sort((a, b) => {
            const indexA = a.index ?? Number.MAX_SAFE_INTEGER;
            const indexB = b.index ?? Number.MAX_SAFE_INTEGER;
            return indexA - indexB;
        });
        sortedVariations.forEach((variation, index) => {
            this.variationsArray?.push(new CustomFormGroup<ItemsVariationsWithCustomVariationsValue>({
                name: new CustomFormControl(
                    variation.name || null,
                    { validators: [Validators.required, Validators.maxLength(this.validators.nameMaxLength)] }, 
                ),
                alternative_names: new CustomFormControl(
                    (variation.alternative_names || []).map(alt => ({...alt}))
                ),
                id: new CustomFormControl(
                    variation.id || null
                ),
                tmp_id: new CustomFormControl(
                    generateUuid() //serve per la sincronizzazione con i valori variante (NON aggiungerlo al body per POST/PUT)
                ),
                default_exit: new CustomFormControl(
                    variation.default_exit || null
                ),
                required: new CustomFormControl(
                    variation.required || false
                ),
                min_quantity: new CustomFormControl(
                    variation.min_quantity || null
                ),
                max_quantity: new CustomFormControl(
                    variation.max_quantity || null
                ),
                variation_values: new CustomFormArray<CustomFormGroup<ItemsVariationValuesFormArray>>(
                    []
                ),
                index: new CustomFormControl(
                    index + 1
                ),
                disable_quantity_value: new CustomFormControl(
                    variation.disable_quantity_value || false
                ),
                expanded: new CustomFormControl(false)
            }))

            if(variation.variation_values?.length) {
                this.initVariationValue(variation.variation_values, this.variationsArray?.length? this.variationsArray.length -1 : 0);
            }
        })
    }

    initVariationValue(variationValues: ItemsVariationValues[], variationIndex: number) {
        const sortedVariationValues = [...variationValues].sort((a, b) => {
            const indexA = a.index ?? Number.MAX_SAFE_INTEGER;
            const indexB = b.index ?? Number.MAX_SAFE_INTEGER;
            return indexA - indexB;
        });
        sortedVariationValues.forEach((variationValue, index) => {
            this.getVariationValues(variationIndex)?.push(new CustomFormGroup<ItemsVariationValuesFormArray>({
                value: new CustomFormControl(
                    variationValue.value || null,
                    { validators: [Validators.required, Validators.maxLength(this.validators.nameVariantValueMaxLength)] }, 
                ),
                alternative_names: new CustomFormControl(
                    (variationValue.alternative_names || []).map(alt => ({...alt}))
                ),
                id: new CustomFormControl(
                    variationValue.id || null
                ),
                tmp_id: new CustomFormControl(
                    generateUuid()
                ),
                price_difference: new CustomFormControl(
                    variationValue.price_difference || null
                ),
                price_difference_type: new CustomFormControl(
                    variationValue.price_difference_type || null
                ),
                default_value: new CustomFormControl(
                    variationValue.default_value || false
                ),
                _linkedItemName: new CustomFormControl(
                    variationValue.linked_item_uuid? 
                        this.variationLinkableItems.find(item => item.uuid == variationValue.linked_item_uuid)?.name || null : null
                ),
                linked_item_uuid: new CustomFormControl(
                    variationValue.linked_item_uuid || null
                ),
                _image: new CustomFormControl(
                    variationValue.image_url ? {image_url: variationValue.image_url, uploaded: true} : null
                ),
                image_url: new CustomFormControl(
                    variationValue.image_url || null
                ),
                unit_stock_quantity: new CustomFormControl(
                    variationValue.unit_stock_quantity || null
                ),
                index: new CustomFormControl(
                    index + 1
                ),
                expanded: new CustomFormControl(false)
            }))
        })
    }

    getVariationsFields(): Partial<ItemVariationsFields>[] {
        // Copio l'oggetto di destinazione per non mutare l'originale
        let updatedVariations = [...this.variationsArray?.value || []] as Partial<ItemVariationsFields>[]; 

        // Recupero tutte le chiavi (nomi dei campi) presenti nel form
        const variationsFieldsToMap = Object.keys(this.variationsForm?.controls || {});

        // Ciclo su tutte le chiavi trovate nel form
        variationsFieldsToMap.forEach(key => {
            const control = this.variationsForm.get(key);

            if(control) {
                let value = control.value;

                if (key === 'variations' && Array.isArray(value)) {
                    // Creazione mappa <ID: variante> per le varianti dell'item già salvate 
                    const initialVarsMap = new Map(this.initialVariations.map(v => [v.id, v]));
            
                    updatedVariations = this.variationsArray.value.map((variation: Partial<ItemVariationsFields>) => {
                        // Trova la variante già salvata per l'item (o oggetto vuoto se non trovato)
                        const initial = variation.id? initialVarsMap.get(variation.id): undefined;
            
                        // Creazione mappa <ID: valore_variante> per i valori della variante già salvati
                        const initialValuesMap = new Map((initial?.variation_values || []).map(iv => [iv.id, iv]));
            
                        // Gestione VALORI VARIANTE
                        const variation_values = (variation.variation_values || []).map((variationValue: ItemsVariationValues) => {
                            const initialVariationValue = (variationValue.id ? initialValuesMap.get(variationValue.id) : undefined) || {};
            
                            const rawVariationValue: ItemsVariationValues = {
                                ...initialVariationValue,
                                value: variationValue.value,
                                alternative_names: (variationValue.alternative_names || []) as Array<AlternativeTranslate>,
                                price_difference: variationValue.price_difference,
                                price_difference_type: variationValue.price_difference_type,
                                linked_item_uuid: variationValue.linked_item_uuid,
                                unit_stock_quantity: variationValue.unit_stock_quantity,
                                image_url: variationValue.image_url,
                                default_value: variationValue.default_value,
                                index: variationValue.index
                            }
                            return rawVariationValue;
                        })
            
                        const rawVariation: ItemVariationsFields = {
                            ...(initial || {}),
                            name: variation.name || "",
                            alternative_names: (variation.alternative_names || []) as Array<AlternativeTranslate>,
                            default_exit: variation.default_exit,
                            required: variation.required,
                            min_quantity: variation.min_quantity,
                            max_quantity: variation.max_quantity,
                            disable_quantity_value: variation.disable_quantity_value,
                            index: variation.index,
                            variation_values
                        };
            
                        return rawVariation; 
                    });
                }
            }
        })

        return updatedVariations;
    }

    get variationsArray() {
        return this.variationsForm?.controls.variations as VariationsFormGroup;
    }

    getNameVariation(index: number) {
        return this.variationsArray?.at(index).controls.name;
    }

    getAlternativeNamesVariation(index: number) {
        return this.variationsArray?.at(index).controls.alternative_names;
    }

    getTmpIdVariation(index: number) {
        return this.variationsArray?.at(index).controls.tmp_id;
    }

    getDefaultExitVariation(index: number) {
        return this.variationsArray?.at(index).controls.default_exit;
    }

    getRequiredVariation(index: number) {
        return this.variationsArray?.at(index).controls.required;
    }

    getMinQuantityVariation(index: number) {
        return this.variationsArray?.at(index).controls.min_quantity;
    }

    getMaxQuantityVariation(index: number) {
        return this.variationsArray?.at(index).controls.max_quantity;
    }

    getDisableQuantityValueVariation(index: number) {
        return this.variationsArray?.at(index).controls.disable_quantity_value;
    }

    getExpandedVariation(index: number) {
        return this.variationsArray?.at(index).controls.expanded;
    }

    getVariationValueSearch(index: number) {
        return this.variationsArray?.at(index).controls.variation_value_search;
    }

    getVariationValues(index: number) {
        return this.variationsArray?.at(index).controls.variation_values;
    }

    getNameVariationValues(index: number, valueIndex: number) {
        return this.getVariationValues(index)?.at(valueIndex).controls.value;
    }

    getAlternativeNamesVariationValues(index: number, valueIndex: number) {
        return this.getVariationValues(index)?.at(valueIndex).controls.alternative_names;
    }

    getTmpIdVariationValues(index: number, valueIndex: number) {
        return this.getVariationValues(index)?.at(valueIndex).controls.tmp_id;
    }

    getPriceDifferenceVariationValues(index: number, valueIndex: number) {
        return this.getVariationValues(index)?.at(valueIndex).controls.price_difference;
    }

    getPriceDifferenceTypeVariationValues(index: number, valueIndex: number) {
        return this.getVariationValues(index)?.at(valueIndex).controls.price_difference_type;
    }

    getLinkedItemNameVariationValues(index: number, valueIndex: number) {
        return this.getVariationValues(index)?.at(valueIndex).controls._linkedItemName;
    }

    getLinkedItemUUIDVariationValues(index: number, valueIndex: number) {
        return this.getVariationValues(index)?.at(valueIndex).controls.linked_item_uuid;
    }

    getImageVariationValues(index: number, valueIndex: number) {
        return this.getVariationValues(index)?.at(valueIndex).controls._image;
    }

    getImageUrlVariationValues(index: number, valueIndex: number) {
        return this.getVariationValues(index)?.at(valueIndex).controls.image_url;
    }

    getDefaultValueVariationValues(index: number, valueIndex: number) {
        return this.getVariationValues(index)?.at(valueIndex).controls.default_value;
    }

    getUnitStockQuantityVariationValues(index: number, valueIndex: number) {
        return this.getVariationValues(index)?.at(valueIndex).controls.unit_stock_quantity;
    }

    getExpandedVariationValues(index: number, valueIndex: number) {
        return this.getVariationValues(index)?.at(valueIndex).controls.expanded;
    }
}